import React, { SetStateAction, Dispatch, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { Store } from 'redux';
import { Provider } from 'react-redux';
import { createPortal } from 'react-dom';
import { get, partial } from 'lodash';
import { Dispatch as ReduxDispatch } from '@reduxjs/toolkit';

import { StateFunction } from '~/types/redux/share';
import RecordController from '~/App/Controllers/RecordController';
import SweetAlertApp from '~/App/Components/SweetAlert/App';
import Modal from '~/App/Components/Modal';
import Recorder from './Recorder';
import { Reply } from '~/types/models/reply';
import { stopAllVideo } from '~/utils/video';

type CreateReply = () => Promise<Reply | undefined>;
type CreateReplyRedux = () => (
  dispatch: ReduxDispatch,
  getState: StateFunction
) => Promise<Reply | undefined>;

export type PopupRecorderProps = {
  isOpen?: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  createReplyForVideoReply: CreateReply | CreateReplyRedux;
  onVideoReplySuccess: (reply: Reply) => void;
};

const Container = styled.div`
  flex-direction: row;
  align-items: normal;
  justify-content: center;
  background-color: transparent;

  @media (max-width: 1499px) {
    flex-direction: column;
  }
`;

const StyledModal = styled(Modal)`
  margin: 120px auto;
  width: fit-content;
  @media (max-width: 768px) {
    width: max-content !important;
    padding: unset;
  }
  > div {
    width: 450px;
    height: 450px;
    padding: unset;
    border: 0;
    max-height: 100vw;
    max-width: calc(100vw - 20px);
    background-color: transparent;
  }
`;

const RecorderContainer = styled.div`
  width: 100% !important;
  height: 100%;
  box-shadow: unset !important;
  padding: unset !important;

  div > .recorder-video {
    width: 100%;
    height: 100%;
    border-radius: 20px;

    > .video-inner {
      width: 100%;
      height: 100%;
      position: unset;
    }
  }
`;

export default function PopupRecorder({
  isOpen = true,
  setOpen,
  onVideoReplySuccess,
  createReplyForVideoReply,
}: PopupRecorderProps) {
  const onRequestClose = partial(setOpen, false);
  const [store, setStore] = useState<Store<Record<string, unknown>>>();

  useEffect(() => {
    async function bootstrap() {
      const newStore = await new RecordController().record();
      setStore(newStore);
    }

    if (isOpen) {
      stopAllVideo();
      bootstrap();
    }
  }, [isOpen]);

  return (
    <StyledModal isOpen={isOpen} onRequestClose={onRequestClose} hideActions>
      {typeof store !== 'undefined' && (
        <Provider store={store}>
          <Container id='popup-recorder'>
            <RecorderContainer className='bg-white flex flex-col items-center h-full gap-y-4 p-10 rounded-lg overflow-hidden'>
              <Recorder
                createReplyForVideoReply={createReplyForVideoReply}
                onCloseRecorder={() => setOpen(false)}
                onVideoReplySuccess={onVideoReplySuccess}
              />
            </RecorderContainer>
          </Container>
          {createPortal(
            <SweetAlertApp
              stateResolver={(state: Record<string, unknown>) =>
                get(state, 'sweetAlert')
              }
            />,
            document.getElementById('sweet-alert-target') as HTMLElement
          )}
        </Provider>
      )}
    </StyledModal>
  );
}
